/**
 * Extension Auth Page
 * 
 * This page is opened by the extension for authentication.
 * After the user signs in, it sends the token back to the extension and closes.
 */

import { useEffect, useState, FormEvent } from 'react';
import { useAuthActions, useAuthToken } from '@convex-dev/auth/react';
import { useConvexAuth, useQuery } from 'convex/react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Logo, CheckCircle, Loader2, Input } from '@uprosal/ui';
import { Mail, Eye, EyeOff } from 'lucide-react';
import { api } from 'convex/_generated/api';

// Google Icon SVG component
function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );
}

export function ExtensionAuthPage() {
    const { signIn } = useAuthActions();
    const { isAuthenticated, isLoading } = useConvexAuth();
    const authToken = useAuthToken();
    const [status, setStatus] = useState<'idle' | 'signing-in' | 'sending-token' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const convexUrl = import.meta.env.VITE_CONVEX_URL as string | undefined;

    // Email/Password state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Query user context after auth
    const userContext = useQuery(api.extensionContext.getExtensionContext, isAuthenticated ? {} : 'skip');

    // Persist extensionId before OAuth redirect (it gets lost after Google callback)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const extensionIdFromUrl = urlParams.get('extensionId');
        if (extensionIdFromUrl) {
            sessionStorage.setItem('upzora_extension_id', extensionIdFromUrl);
        }
    }, []);

    // Get extensionId from URL or sessionStorage (fallback after OAuth redirect)
    const getExtensionId = (): string | null => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('extensionId') || sessionStorage.getItem('upzora_extension_id');
    };

    // Force light theme for auth page
    useEffect(() => {
        const html = document.documentElement;
        html.classList.remove('dark');
        return () => {
            html.classList.add('dark');
        };
    }, []);

    // When authenticated AND we have user context, send to extension
    useEffect(() => {
        if (isAuthenticated && (status === 'idle' || status === 'signing-in') && userContext !== undefined) {
            sendTokenToExtension();
        }
    }, [isAuthenticated, status, userContext, authToken]);

    const handleGoogleSignIn = async () => {
        setStatus('signing-in');
        try {
            // Pass redirectTo so OAuth callback returns to this page, not dashboard
            await signIn('google', { redirectTo: '/auth/extension' });
        } catch (error) {
            setStatus('error');
            setErrorMessage('Failed to sign in. Please try again.');
        }
    };

    const handleEmailPasswordSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage('');
        setStatus('signing-in');

        try {
            await signIn('password', {
                email,
                password,
                flow: 'signIn'
            });
        } catch (err) {
            setStatus('error');
            setErrorMessage(err instanceof Error ? err.message : 'Authentication failed');
        }
    };

    const sendTokenToExtension = async () => {
        setStatus('sending-token');

        try {
            if (!authToken) {
                setStatus('error');
                setErrorMessage('Authentication token not available. Please refresh and try again.');
                return;
            }

            // Send message to extension via chrome.runtime.sendMessage
            if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
                const extensionId = getExtensionId();

                if (extensionId) {
                    try {
                        await chrome.runtime.sendMessage(extensionId, {
                            type: 'EXTENSION_AUTH_COMPLETE',
                            userContext: userContext || null,
                            convexUrl: convexUrl || null,
                            token: authToken,
                        });
                        console.log('[Uprosal] Sent auth complete to extension:', extensionId, userContext);
                        sessionStorage.removeItem('upzora_extension_id');
                    } catch (e) {
                        console.warn('[Uprosal] Could not send to extension:', e);
                    }
                } else {
                    console.warn('[Uprosal] No extensionId found in URL or sessionStorage');
                }
            }

            setStatus('success');
            setTimeout(() => {
                window.close();
            }, 2000);
        } catch (error) {
            console.error('Token send error:', error);
            setStatus('error');
            setErrorMessage('Failed to complete authentication.');
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // Success state
    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-background">
                <div className="absolute inset-0 bg-linear-to-br from-white via-green-50/30 to-cyan-50/40" />
                <Card className="glass-card max-w-sm relative z-10">
                    <CardContent className="pt-8 pb-6 text-center space-y-4">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-foreground">Success!</h2>
                            <p className="text-muted-foreground mt-1">
                                You're now signed in. This window will close automatically.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error state
    if (status === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-background">
                <div className="absolute inset-0 bg-linear-to-br from-white via-red-50/30 to-orange-50/40" />
                <Card className="glass-card max-w-sm relative z-10">
                    <CardContent className="pt-8 pb-6 text-center space-y-4">
                        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                            <span className="text-2xl text-destructive">!</span>
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-foreground">Error</h2>
                            <p className="text-muted-foreground mt-1">{errorMessage}</p>
                        </div>
                        <Button onClick={() => setStatus('idle')} variant="outline">
                            Try Again
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Sign-in state
    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-background">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-linear-to-br from-white via-green-50/30 to-cyan-50/40" />

            {/* Subtle blur blobs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl" />

            {/* Content */}
            <div className="relative z-10 w-full max-w-sm space-y-6">
                {/* Logo & Tagline */}
                <div className="text-center space-y-3">
                    <div className="flex justify-center">
                        <Logo size="lg" showText={false} />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground tracking-tight">
                        Upzora Extension
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Sign in to connect your extension
                    </p>
                </div>

                {/* Auth Card */}
                <Card className="glass-card">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="text-lg">Sign in to continue</CardTitle>
                        <CardDescription>Connect your account to the extension</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <>
                            {errorMessage && (
                                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                    {errorMessage}
                                </div>
                            )}

                            {/* Google OAuth */}
                            <Button
                                onClick={handleGoogleSignIn}
                                variant="outline"
                                className="w-full h-11 text-base font-medium"
                                disabled={status === 'signing-in'}
                            >
                                {status === 'signing-in' ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    <>
                                        <GoogleIcon className="mr-2 h-5 w-5" />
                                        Continue with Google
                                    </>
                                )}
                            </Button>

                            {/* Divider */}
                            <div className="relative my-4">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-card px-2 text-muted-foreground">
                                        Or continue with email
                                    </span>
                                </div>
                            </div>

                            {/* Email/Password form */}
                            <form onSubmit={handleEmailPasswordSubmit} className="space-y-3">
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                />
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <Button type="submit" className="w-full h-11" disabled={status === 'signing-in'}>
                                    <Mail className="mr-2 h-4 w-4" />
                                    {status === 'signing-in' ? 'Loading...' : 'Sign In'}
                                </Button>
                            </form>
                        </>

                        <p className="text-center text-xs text-muted-foreground">
                            This window will close after sign-in
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
